import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { FC, ReactNode, useState } from 'react'
import {
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  useNotify,
  useRefresh,
  useTranslate,
  useUpdate,
} from 'react-admin'

interface EditRequestDialogProps {
  recordId: string | undefined
  samenwerkingTitel?: string | null
  actieverzoekTitel?: string | null
  currentStatus: string | null | undefined
  renderTrigger: (open: () => void) => ReactNode
}

const statusOptions = [
  { id: 'OPEN', name: 'OPEN' },
  { id: 'IN_BEHANDELING', name: 'IN BEHANDELING' },
  { id: 'GEREEDGEMELD', name: 'GEREED GEMELD' },
  { id: 'GEREED', name: 'GEREED' },
  { id: 'GEWEIGERD', name: 'GEWEIGERD' },
  { id: 'INGETROKKEN', name: 'INGETROKKEN' },
]

const EditRequestDialog: FC<EditRequestDialogProps> = ({
  recordId,
  currentStatus,
  samenwerkingTitel,
  actieverzoekTitel,
  renderTrigger,
}) => {
  const [open, setOpen] = useState(false)
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const [update, { isLoading, error }] = useUpdate()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const save = (values: Record<string, unknown>) => {
    void update(
      'actieverzoeken',
      { id: recordId, data: { status: values.status, melding: values.melding }, previousData: {} },
      {
        onSuccess: () => {
          notify('ra.notification.updated', { type: 'info' })
          handleClose()
          refresh()
        },
        onError: (err: unknown) => {
          const message = err instanceof Error ? err.message : 'ra.notification.http_error'
          notify(message, { type: 'warning' })
        },
      }
    )
  }

  return (
    <>
      {renderTrigger(handleOpen)}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {translate('resources.requests.editStatus')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 0.5 }}>{samenwerkingTitel}</Typography>
          <Typography variant="subtitle2" sx={{ mb: 3 }}>{actieverzoekTitel}</Typography>

          <SimpleForm
            onSubmit={save}
            toolbar={
              <Toolbar>
                <SaveButton disabled={isLoading} />
                <Button onClick={handleClose}>{translate('ra.action.cancel')}</Button>
              </Toolbar>
            }
            defaultValues={{ status: currentStatus }}
          >
            <SelectInput
              source="status"
              label="Status"
              fullWidth
              variant="outlined"
              choices={statusOptions}
              defaultValue={currentStatus}
              helperText={translate('resources.requests.statusWarning')}
              required
              validate={required()}
            />
            <TextInput source="melding" fullWidth label="Melding" />
          </SimpleForm>
          {!!error && (
            <p style={{ color: 'red' }}>{ error.message }</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default EditRequestDialog
